import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { getAppNameFromInAppBrowser } from 'helpers/getAppNameFromInAppBrowser'
import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'
import { saveFileToDevice } from 'helpers/saveFileToDevice'

import { TPageProps } from 'models/common.model'

import { eventLogger } from 'services/eventLogger.service'

import downloadRoundIcon from 'assets/images/sprite/download-round-icon.svg'
import goldEllipseIcon from 'assets/images/sprite/gold-ellipse-icon.svg'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import { PlatformOS } from 'root-constants'

import { StyledDownloadWellness as S } from './DownloadWellness.styles'
import { WELLNESS_DOWNLOADS, WELLNESS_DOWNLOADS_ARCHIVE_URL } from './constants'

export const DownloadWellness: React.FC<TPageProps> = ({ nextPagePath }) => {
  const { t } = useTranslation()
  const { search } = useLocation()

  const [downloadingFileUrl, setDownloadingFileUrl] = useState('')
  const [downloadedFileUrls, setDownloadedFileUrls] = useState<string[]>([])

  const isMobileDevice = getMobileOperatingSystem() !== PlatformOS.UNKNOWN
  const isInAppBrowser = !!getAppNameFromInAppBrowser()

  const downloadFile = async (fileUrl: string) => {
    const fileName = fileUrl.split('/').pop() as string

    eventLogger.logDownloadFileTap(fileName)

    if (isMobileDevice) {
      window.open(fileUrl, '_blank')
      return
    }

    setDownloadingFileUrl(fileUrl)

    const isFileSaved = await saveFileToDevice(fileUrl, fileName)

    setDownloadingFileUrl('')

    if (isFileSaved) {
      setDownloadedFileUrls((fileUrls) => [...fileUrls, fileUrl])
    }
  }

  const isArchiveDownloading =
    downloadingFileUrl === WELLNESS_DOWNLOADS_ARCHIVE_URL

  const handleDownloadAll = () => downloadFile(WELLNESS_DOWNLOADS_ARCHIVE_URL)

  const handleContinue = () => {
    goTo(nextPagePath || `${PageId.APP_WELLNESS}${search}`)
  }

  return (
    <S.Wrapper>
      <S.Content>
        <S.Title>
          <Trans i18nKey="wellnessDownload.title" />
        </S.Title>

        <S.DownloadList>
          {WELLNESS_DOWNLOADS.map(
            ({ titleKey, coverImage, bonusNumber, fileUrl }) => {
              const isFileDownloading = downloadingFileUrl === fileUrl
              const isFileDownloaded = downloadedFileUrls.includes(fileUrl)

              return (
                <S.DownloadCard key={titleKey}>
                  <S.CoverImage src={coverImage} alt="" />

                  <S.CardInfo>
                    {!!bonusNumber && (
                      <S.BonusLabel>
                        <S.BonusLabelBullet
                          svg={goldEllipseIcon}
                          width={4}
                          height={4}
                        />
                        {t('wellnessDownload.bonusLabel', {
                          number: bonusNumber,
                        })}
                      </S.BonusLabel>
                    )}

                    <S.CardTitle>{t(titleKey)}</S.CardTitle>

                    {isFileDownloading && (
                      <S.DownloadStatus>
                        {t('wellnessDownload.downloading')}
                      </S.DownloadStatus>
                    )}

                    {isFileDownloaded && (
                      <S.DownloadStatus>
                        {t('wellnessDownload.downloaded')}
                      </S.DownloadStatus>
                    )}
                  </S.CardInfo>

                  <S.DownloadFileButton
                    type="button"
                    disabled={isFileDownloading}
                    onClick={() => downloadFile(fileUrl)}
                  >
                    {isFileDownloading ? (
                      <S.DownloadSpinner />
                    ) : (
                      <S.DownloadIcon
                        svg={downloadRoundIcon}
                        width={40}
                        height={40}
                      />
                    )}
                  </S.DownloadFileButton>
                </S.DownloadCard>
              )
            },
          )}
        </S.DownloadList>

        <S.ButtonsContainer>
          {!isInAppBrowser && (
            <S.DownloadAllButton
              type="button"
              disabled={isArchiveDownloading}
              onClick={handleDownloadAll}
            >
              {isArchiveDownloading
                ? t('wellnessDownload.downloading')
                : t('wellnessDownload.downloadAll')}
            </S.DownloadAllButton>
          )}

          <S.ContinueButton type="button" onClick={handleContinue}>
            <Trans i18nKey="actions.continue" />
          </S.ContinueButton>
        </S.ButtonsContainer>
      </S.Content>
    </S.Wrapper>
  )
}
